import {
  ClientSideNotifications,
  INotificationChatResponse,
  INotificationResponse,
  Notifications,
  NotificationsChat,
} from 'types';
import { pull } from 'lodash';
import { io, Socket } from 'socket.io-client';
import { chatClientSideConnectionListeners, defaultChatListeners } from 'constants/webSocket';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PlatformType } from 'hooks';
import { webSocketService } from './WebSocketService';

class ChatWebSocketService {
  ws: Socket | null = null;

  connectInterval: number | null = null;

  pingInterval: number | null = null;

  timeout = 250;

  token: string | null = null;

  isConnecting: boolean = false;

  listeners: {
    [key in NotificationsChat]: Array<(notification: INotificationChatResponse<any>) => void>;
  } = defaultChatListeners as {
    [key in NotificationsChat]: Array<(notification: INotificationChatResponse<any>) => void>;
  };

  socketConnectionClientListeners: {
    [key in ClientSideNotifications]: Array<(notification: string) => void>;
  } = chatClientSideConnectionListeners as {
    [key in ClientSideNotifications]: Array<(notification: string) => void>;
  };

  manuallyClosed = false;

  connect = (token: string) => {
    this.cleanupSocket();

    this.token = token;
    this.isConnecting = true;
    const socket = io(process.env.REACT_APP_CHAT_WEBSOCKET_URL as string, {
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

    socket.on('connect', () => {
      console.log('Websocket chat connected');
      this.ws = socket;
      this.manuallyClosed = false;

      this.isConnecting = false;

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
      }
      this.pingInterval = setInterval(() => {
        this.sendMessage({
          type: 'PING',
        });
      }, 30000) as any;

      this.timeout = 250;
      if (this.connectInterval) {
        clearTimeout(this.connectInterval);
      }
    });

    socket.on('disconnect', () => {
      console.log('Websocket chat closed');

      if (!this.manuallyClosed) {
        this.timeout += this.timeout;
        this.connectInterval = setTimeout(
          () => this.retry(token),
          Math.min(10000, this.timeout),
        ) as any;
      }

      this.ws?.close();

      if (!webSocketService.isConnected()) {
        this.cleanupSocket();
      }

      // this.listeners = defaultChatListeners as {
      //   [key in NotificationsChat]: Array<(notification: INotificationChatResponse<any>) => void>;
      // };

      this.listeners = Object.keys(NotificationsChat).reduce(
        (acc, cur) => ({
          ...acc,
          [cur]: [],
        }),
        {},
      ) as {
        [key in NotificationsChat]: Array<(notification: INotificationChatResponse<any>) => void>;
      };

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
      }
    });

    socket.on('error', (err) => {
      console.log('Websocket error', err);

      socket.close();
      this.isConnecting = false;
    });

    socket.on('connect_error', (err) => {
      console.log('Connect error: ', err);
      this.isConnecting = false;
    });

    Object.keys(this.listeners).forEach((listenerKey) =>
      socket.on(listenerKey, (event) => {
        this.listeners[listenerKey as NotificationsChat].forEach((listener) => listener(event));
      }),
    );

    Object.keys(this.socketConnectionClientListeners).forEach((listenerKey) =>
      socket.on(listenerKey, () => {
        this.socketConnectionClientListeners[listenerKey as ClientSideNotifications].forEach(
          (listener) => listener(listenerKey),
        );
      }),
    );
  };

  onMessage(
    listener: (notification: INotificationChatResponse<any>) => void,
    type: NotificationsChat,
  ) {
    this.listeners[type].push(listener);
    return () => {
      pull(this.listeners[type], listener);
    };
  }

  onClientMessage(listener: (notification: string) => void, type: ClientSideNotifications) {
    this.socketConnectionClientListeners[type].push(listener);
    return () => {
      pull(this.socketConnectionClientListeners[type], listener);
    };
  }

  sendMessage(message: { type: string; data?: object }) {
    console.log('Websocket send message', message);
    const { ws, token } = this;
    return new Promise((resolve) => {
      if (ws) {
        ws.emit(message.type, message, (response: INotificationResponse<any>) => resolve(response));
      }
    });
  }

  retry = (token: string) => {
    console.log('Websocket retry connect');
    const access_token = localStorage.getItem('access_token');
    const { ws } = this;

    if (!ws || ws.connected === false) {
      if (access_token && token) {
        this.connect(token);
      }
    }
  };

  isConnected() {
    return this.ws && this.ws.connected === true;
  }

  isChatConnecting() {
    return this.isConnecting === true;
  }

  disconnect() {
    this.manuallyClosed = true;
    this.ws?.close();
    this.cleanupSocket();
  }

  cleanupSocket() {
    if (this.ws) {
      this.ws.removeAllListeners();
      this.ws = null;
    }

    this.token = null;
    this.isConnecting = false;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.connectInterval) {
      clearTimeout(this.connectInterval);
      this.connectInterval = null;
    }
  }
}

export const chatWebSocketService = new ChatWebSocketService();
